import React, { useEffect, useState } from "react";
import ModalShell from "../ModalShell";
import { stockRequestBody } from "../../shared/api/stockContext";
import { getMessageForFetchError } from "../../shared/utils/apiError";


import { apiFetch } from "../../shared/api/apiFetch";

function StockBuyRecommendationFilterModal({
  open,
  onClose,
  stockSession,
  currentUserId,
  serverUrl,
  onSaved,
  onError,
}) {
  const [name, setName] = useState("");
  const [yahooUrl, setYahooUrl] = useState("");
  const [previewRows, setPreviewRows] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName("");
    setYahooUrl("");
    setPreviewRows(null);
    onError("");
  }, [open, onError]);

  async function previewFetch() {
    const url = yahooUrl.trim();
    if (!url) {
      onError("Yahoo portfolio URL is required.");
      return;
    }
    setPreviewing(true);
    onError("");
    try {
      const resp = await apiFetch(`${serverUrl}/previewStockBuyRecommendations`, {
        method: "POST",
        body: JSON.stringify(stockRequestBody(stockSession, { yahooUrl: url })),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(data.message || data.error || "Preview failed");
      }
      setPreviewRows(Array.isArray(data.rows) ? data.rows : []);
    } catch (err) {
      onError(getMessageForFetchError(err));
      setPreviewRows(null);
    } finally {
      setPreviewing(false);
    }
  }

  async function saveFilter() {
    const trimmedName = name.trim();
    const url = yahooUrl.trim();
    if (!trimmedName) {
      onError("Watchlist name is required.");
      return;
    }
    if (!url) {
      onError("Yahoo portfolio URL is required.");
      return;
    }
    if (!currentUserId) {
      onError("User session is required to save a watchlist.");
      return;
    }
    setSaving(true);
    onError("");
    try {
      const resp = await apiFetch(`${serverUrl}/saveStockBuyRecommendationFilter`, {
        method: "POST",
        body: JSON.stringify(
          stockRequestBody(stockSession, {
            name: trimmedName,
            yahooUrl: url,
            created_by: currentUserId,
          })
        ),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(data.message || data.error || "Failed to save watchlist");
      }
      if (onSaved) {
        await onSaved(data);
      }
      onClose();
    } catch (err) {
      onError(getMessageForFetchError(err));
    } finally {
      setSaving(false);
    }
  }

  const busy = previewing || saving;
  const previewCount = previewRows != null ? previewRows.length : null;

  return (
    <ModalShell
      open={open}
      onClose={busy ? undefined : onClose}
      title="Add Yahoo watchlist"
      subtitle="Save a Yahoo Finance portfolio view as a named Buy List tab."
      maxWidth={768}
      footer={
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap" }}>
          <button type="button" className="btn btn-md btn-secondary" disabled={busy} onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-md btn-primary-outline"
            disabled={busy || !yahooUrl.trim()}
            onClick={() => previewFetch()}
          >
            {previewing ? "Fetching…" : "Fetch preview"}
          </button>
          <button
            type="button"
            className="btn btn-md btn-primary"
            disabled={busy || !name.trim() || !yahooUrl.trim()}
            onClick={() => saveFilter()}
          >
            {saving ? "Saving…" : "Save watchlist"}
          </button>
        </div>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <label>
          <span className="form-label">Name</span>
          <input
            type="text"
            className="form-control"
            value={name}
            maxLength={80}
            placeholder="e.g. ARK, Space"
            onChange={(e) => setName(e.target.value)}
            disabled={busy}
          />
        </label>
        <label style={{ display: "block", width: "100%" }}>
          <span className="form-label">Yahoo portfolio URL</span>
          <input
            type="url"
            className="form-control"
            value={yahooUrl}
            placeholder="https://finance.yahoo.com/portfolio/p_110/view/view_8"
            onChange={(e) => setYahooUrl(e.target.value)}
            disabled={busy}
            style={{ width: "100%", maxWidth: "100%", boxSizing: "border-box" }}
          />
        </label>
        {previewCount != null ? (
          <p className="panel-hint" style={{ margin: 0 }}>
            Preview: {previewCount} matching stock{previewCount === 1 ? "" : "s"} (52-Wk High Chg % &lt; −30% and 52-DMA
            Chg % &gt; 0). Save watchlist to persist as a tab.
          </p>
        ) : (
          <p className="panel-hint" style={{ margin: 0 }}>
            Paste the portfolio <strong>view</strong> URL from Yahoo Finance while logged in. Save runs a full fetch and
            stores results for this watchlist.
          </p>
        )}
      </div>
    </ModalShell>
  );
}

export default StockBuyRecommendationFilterModal;
